import type { TFunction } from 'i18next';
import type { FilterFieldOption } from '~/components/ui';
import type { MyProductFilterOptionsQuery } from '~/graphql/graphql';
import { getStockHealthOptions } from '~/utils/inventory';
import {
  ENUM_OPERATORS,
  NUMBER_OPERATORS,
  newProductFilterRowId,
  STRING_OPERATORS,
  type ProductFilterRow,
} from './filterClauses';
import {
  MY_PRODUCTS_FILTER_VIEWS,
  type MyProductsFilterView,
} from './filterViews';

type MyProductTranslate = TFunction<'my-product'>;

const OPERATOR_LABEL_KEYS = {
  contains: 'filter.operators.contains',
  eq: 'filter.operators.is',
  neq: 'filter.operators.is_not',
  notContain: 'filter.operators.does_not_contain',
  gt: 'filter.operators.greater_than',
  lt: 'filter.operators.less_than',
  gte: 'filter.operators.gte',
  lte: 'filter.operators.lte',
} as const;

type ProductOperator = keyof typeof OPERATOR_LABEL_KEYS;

const operatorOptions = (
  t: MyProductTranslate,
  operators: readonly ProductOperator[]
): FilterFieldOption['operatorOptions'] =>
  operators.map((operator) => ({
    label: t(OPERATOR_LABEL_KEYS[operator]),
    operator,
  }));

const getStringOperators = (t: MyProductTranslate) =>
  operatorOptions(t, STRING_OPERATORS);

const getNumberOperators = (t: MyProductTranslate) =>
  operatorOptions(t, NUMBER_OPERATORS);

const getEnumOperators = (t: MyProductTranslate) =>
  operatorOptions(t, ENUM_OPERATORS);

const getVisibilityOptions = (t: MyProductTranslate) => [
  {
    label: t('filter.visibility.visible_to_buyers'),
    value: 'VISIBLE_TO_BUYERS',
  },
  {
    label: t('filter.visibility.visible_to_my_buyers'),
    value: 'VISIBLE_TO_MY_BUYERS',
  },
  { label: t('filter.visibility.inactive'), value: 'INACTIVE' },
];

const getBaseFilterOptions = (
  data: MyProductFilterOptionsQuery | undefined,
  t: MyProductTranslate,
  tInventory: TFunction<'inventory'>
): FilterFieldOption[] => [
  {
    field: 'visibility',
    label: t('filter.fields.visibility'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions: getVisibilityOptions(t),
    withAllOption: true,
  },
  {
    field: 'productCode',
    label: t('filter.fields.product_code'),
    operatorOptions: getStringOperators(t),
    inputType: 'string',
  },
  {
    field: 'productName',
    label: t('filter.fields.product_name'),
    operatorOptions: getStringOperators(t),
    inputType: 'string',
  },
  {
    field: 'length',
    label: t('filter.fields.length'),
    operatorOptions: getStringOperators(t),
    inputType: 'string',
  },
  {
    field: 'pricePerUnit',
    label: t('filter.fields.price'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },
  {
    field: 'stock',
    label: t('filter.fields.stock'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },
  {
    field: 'status',
    label: t('filter.fields.status'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions: getStockHealthOptions(tInventory),
    withAllOption: true,
  },
  {
    field: 'replenishmentRatePerWeek',
    label: t('filter.fields.replenishment_rate'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },
  {
    field: 'readyToShipDays',
    label: t('filter.fields.ready_to_ship'),
    operatorOptions: getNumberOperators(t),
    inputType: 'number',
  },
  {
    field: 'dimensions',
    label: t('filter.fields.dimensions'),
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions:
      data?.platformProductDimensions
        .filter(
          (dimension) =>
            dimension.nominalThicknessInches && dimension.nominalWidthInches
        )
        .map((dimension) => ({
          label: `${dimension.nominalThicknessInches} X ${dimension.nominalWidthInches}`,
          value: `${dimension.nominalThicknessInches}X${dimension.nominalWidthInches}`,
        })) || [],
    withAllOption: true,
  },
];

type PropertyKey = { key: string; displayName: string };

export function getMyProductsFilterFields({
  t,
  tInventory,
  view,
  filterOptions,
  propertyKeys,
}: {
  t: MyProductTranslate;
  tInventory: TFunction<'inventory'>;
  view: MyProductsFilterView;
  filterOptions: MyProductFilterOptionsQuery | undefined;
  propertyKeys: PropertyKey[];
}): FilterFieldOption[] {
  const viewFields = MY_PRODUCTS_FILTER_VIEWS[view].fields;
  const baseOptions = getBaseFilterOptions(filterOptions, t, tInventory).filter(
    (option) => !viewFields || viewFields.includes(option.field)
  );
  const propertyOptions: FilterFieldOption[] = propertyKeys.map((property) => ({
    field: `properties.${property.key}`,
    label: property.displayName,
    operatorOptions: getEnumOperators(t),
    inputType: 'select',
    selectOptions: [],
  }));
  return [...baseOptions, ...propertyOptions];
}

export function initialValueForField(
  fieldOptions: FilterFieldOption[],
  field: string
): string {
  const option = fieldOptions.find((candidate) => candidate.field === field);
  if (option?.inputType !== 'select') return '';
  return option.selectOptions?.[0]?.value ?? '';
}

export const DEFAULT_PRODUCT_FILTER_ROW: Omit<ProductFilterRow, 'id'> = {
  field: 'productCode',
  operator: 'contains',
  value: '',
  connector: 'and',
};

export function newProductFilterRow(): ProductFilterRow {
  return { ...DEFAULT_PRODUCT_FILTER_ROW, id: newProductFilterRowId() };
}
